import { useQueryParams } from '@fs/zion-router'
import { useOverlay } from '@fs/zion-ui'
import { useCallback, useEffect, useMemo } from 'react'
import { useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import { useR2DispatchContext, useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { checkIfSharedHighlights } from '../../../components/generic/panels/highlightPanelOld/utils/highlightUtils'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { getHighlightSubjectPersonId } from '../../../utils/highlightSubject'

/**
 * @typedef {Object} useSlsHighlightsOnIndexReturnObject
 * @property {Object} sharedHighlightOverlay - The shared highlight overlay object.
 * @property {string} splitScreenImageArk - The split screen image ark string.
 */

/**
 * Custom hook to handle the onSave function from the useSlsHighlights hook.
 * The purpose of this hook is to handle the save function for the highlights panel with some custom logic just for the index view.
 *
 * @returns {useSlsHighlightsOnIndexReturnObject}
 */
const useSlsHighlightsOnIndex = () => {
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const zionSlsState = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const { closeOverlay } = useOverlayContext()
  const { query } = useQueryParams()
  const sharedHighlightOverlay = useOverlay()
  const { thumbnails } = useSharedBrowserStateContext()
  const reassignHighlightFlow = useFeatureFlag('rip_sharedImageViewer_reassignHighlightFlow')

  const splitScreenImageArk = useMemo(
    () => (r2State.splitScreen ? thumbnails?.[r2State.referenceFocusedIndex]?.ark : undefined),
    [r2State?.referenceFocusedIndex, thumbnails, r2State.splitScreen]
  )

  // This function will be triggered when the user clicks the save button on the new generic highlight panel.
  const handleHighlightsSave = useCallback(
    ({
      mainEditHighlights,
      splitScreenEditHighlights,
      mainSelectedHighlights,
      splitScreenSelectedHighlights,
      fieldType,
      activeFieldInfo,
    }) => {
      r2Dispatch({
        type: 'BATCH_DISPATCHES',
        value: [
          {
            type: 'SET_EDIT_HIGHLIGHTS',
            value: mainEditHighlights,
          },
          {
            type: 'SET_REFERENCE_EDIT_HIGHLIGHTS',
            value: splitScreenEditHighlights,
          },
        ],
      })

      const isSharedHighlight = checkIfSharedHighlights({
        mainSelectedHighlights,
        splitScreenSelectedHighlights,
        fieldType: fieldType?.fieldType,
        types: zionSlsState?.types,
        personId: getHighlightSubjectPersonId(r2State, activeFieldInfo),
      })

      // Check if any selected highlight is attached to a field different from the active field
      const hasCrossFieldHighlights =
        reassignHighlightFlow.isOn &&
        mainSelectedHighlights?.some(
          (highlight) =>
            highlight?.originalHighlight?.fieldId && highlight.originalHighlight.fieldId !== activeFieldInfo?.id
        )

      if (isSharedHighlight || hasCrossFieldHighlights) {
        h.setDisplayHighlights({
          mainPartitionKey: zionSlsState.currentImageArk,
          elementIds: [activeFieldInfo?.id],
          highlights: mainEditHighlights,
        })

        if (splitScreenImageArk) {
          h.setDisplayHighlights({
            splitScreenPartitionKey: splitScreenImageArk,
            elementIds: [activeFieldInfo?.id],
            highlights: splitScreenEditHighlights,
          })
        }

        // Keep the original single field for backward compatibility
        r2Dispatch({
          type: 'BATCH_DISPATCHES',
          value: [
            {
              type: 'SET_HIGHLIGHTS_FIELD_INFO',
              value: activeFieldInfo,
            },
            {
              type: 'SET_SELECTED_HIGHLIGHTS',
              value: { main: mainSelectedHighlights, splitScreen: splitScreenSelectedHighlights },
            },
          ],
        })

        sharedHighlightOverlay.open()
      } else {
        closeOverlay('highlightsPanelOld')
        h.setActiveFieldInfo({ id: null, value: null }) // Resetting the active field info in the hook
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      zionSlsState.currentImageArk,
      closeOverlay,
      r2Dispatch,
      sharedHighlightOverlay,
      zionSlsState?.images,
      zionSlsState?.types,
      splitScreenImageArk,
      // Both, since the highlight subject can come from either.
      r2State.selectedPersonId,
      r2State.createdPersonId,
      reassignHighlightFlow.isOn,
    ]
  )

  useEffect(() => {
    if (query?.view === 'index') {
      // Setting our custom validation logic to the onSave function on the useSlsHighlights hook.
      h.setOnSaveFunction(handleHighlightsSave)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    zionSlsState.currentImageArk,
    r2State.selectedPersonId,
    r2State.createdPersonId,
    splitScreenImageArk,
    query?.view,
  ])

  return { sharedHighlightOverlay, splitScreenImageArk }
}

export default useSlsHighlightsOnIndex
